import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errMsg, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSharedLocation } from "@/context/LocationContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MeetupChatPanel from "@/components/MeetupChatPanel";
import { AddRoleCard } from "@/components/AddRoleCard";
import { CalendarDays, Store, BookOpen, Megaphone, MapPin, ArrowRight, Users, Clock, User as UserIcon, X, Heart } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [vendorCount, setVendorCount] = useState(0);
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const { loc, radius } = useSharedLocation();
  const [showMyRsvps, setShowMyRsvps] = useState(false);
  const [rsvpsTab, setRsvpsTab] = useState("meetups"); // "meetups" | "events"
  const [openMeetup, setOpenMeetup] = useState(null);
  const [bundlePending, setBundlePending] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [familyActivity, setFamilyActivity] = useState([]);

  // Dedupe display name — some accounts have a doubled `name` field (e.g.
  // "Johan Potgieter Johan Potgieter" from a first_name/surname rebuild). If
  // the trimmed name splits into two identical halves, we show just one half.
  const displayName = (() => {
    const raw = (user?.name || "").trim();
    if (!raw) return "";
    const parts = raw.split(/\s+/);
    if (parts.length >= 2 && parts.length % 2 === 0) {
      const half = parts.length / 2;
      const left = parts.slice(0, half).join(" ");
      const right = parts.slice(half).join(" ");
      if (left && left.toLowerCase() === right.toLowerCase()) return left;
    }
    return raw;
  })();

  useEffect(() => {
    if (!user?.bundle_member) return;
    api.get("/auth/my-roles")
      .then((r) => setBundlePending((r.data.roles || []).filter(
        (x) => x.bundle_member && x.role !== "parent" && x.vendor_status !== "approved")))
      .catch(() => {});
  }, [user?.bundle_member, user?.user_id]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role !== "parent") return;
    api.get("/saved-items").then((r) => setSavedItems(r.data)).catch(() => {});
    if (user?.partner_user_id) api.get("/family/activity").then((r) => setFamilyActivity(r.data)).catch(() => {});
  }, [user?.role, user?.user_id, user?.partner_user_id]);

  const removeSaved = async (item) => {
    try {
      await api.delete(`/saved-items/${item.item_type}/${item.item_id}`);
      setSavedItems((list) => list.filter((i) => !(i.item_type === item.item_type && i.item_id === item.item_id)));
      toast.success("Removed from your list");
    } catch (e) {
      toast.error(errMsg(e, "Could not remove"));
    }
  };

  useEffect(() => {
    let stale = false;
    const params = loc ? `?lat=${loc.lat}&lng=${loc.lng}&radius_km=${radius}` : "";
    api.get(`/events${params}`).then((r) => { if (!stale) setNearbyEvents(r.data); }).catch(() => {});
    api.get(`/vendors${params}`)
      .then((r) => { if (!stale) setVendorCount(loc ? r.data.filter((v) => v.distance_km != null).length : r.data.length); })
      .catch(() => {});
    return () => { stale = true; };
  }, [loc, radius]);

  const today = new Date().toISOString().slice(0, 10);
  const futureSorted = nearbyEvents
    .filter((e) => (e.date || e.event_date || "") >= today)
    .sort((a, b) => (a.date || a.event_date || "").localeCompare(b.date || b.event_date || ""));
  const upcomingEvents = futureSorted.filter((e) => (e.kind || "event") !== "meetup").slice(0, 5);
  const upcomingMeetups = futureSorted.filter((e) => (e.kind || "event") === "meetup").slice(0, 5);
  const myRsvps = events.filter((e) => e.my_rsvp && e.date >= today);
  const myRsvpedMeetups = myRsvps.filter((e) => (e.kind || "event") === "meetup");
  const myRsvpedEvents = myRsvps.filter((e) => (e.kind || "event") !== "meetup");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="parent-dashboard">
      <div className="mb-8 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Parent dashboard</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1" data-testid="dashboard-greeting">Hello, {displayName}</h1>
        <p className="text-forest/60 mt-1">Here's what's happening in your community.</p>
      </div>

      {bundlePending.length > 0 && <BundlePendingCard roles={bundlePending} />}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Upcoming events", value: nearbyEvents.filter((e) => e.date >= today).length, note: loc ? `within ${radius} km` : "", icon: CalendarDays, testid: "stat-events", onClick: () => navigate("/events") },
          { label: "My RSVPs", value: myRsvps.length, icon: Users, testid: "stat-rsvps", onClick: () => setShowMyRsvps((v) => !v), active: showMyRsvps },
          { label: "Local vendors", value: vendorCount, note: loc ? `within ${radius} km` : "", icon: Store, testid: "stat-vendors", onClick: () => navigate("/vendors") },
          { label: "Liked Vendors and Marketplaces", value: savedItems.length, icon: Heart, testid: "stat-saved", onClick: () => setShowSaved((v) => !v), active: showSaved },
        ].map((s) => {
          const Wrapper = s.onClick ? "button" : "div";
          return (
            <Wrapper
              key={s.label}
              type={s.onClick ? "button" : undefined}
              onClick={s.onClick}
              aria-expanded={s.onClick ? s.active : undefined}
              className={`bg-white/70 border rounded-2xl p-5 flex items-center gap-4 shadow-sm text-left w-full transition-colors ${
                s.active ? "border-sage-dark ring-2 ring-sage/30" : "border-sage/20"
              } ${s.onClick ? "hover:bg-sage-light/40 cursor-pointer" : ""}`}
              data-testid={s.testid}
            >
              <span className="w-11 h-11 rounded-xl bg-sage-light flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5 text-sage-dark" />
              </span>
              <div>
                <div className="text-2xl font-black text-forest font-heading">{s.value}</div>
                <div className="text-xs text-forest/60 font-semibold">{s.label}{typeof s.active === "boolean" ? (s.active ? " · Hide" : " · View") : ""}</div>
                {s.note && <div className="text-[10px] text-forest/40 font-semibold" data-testid={`${s.testid}-radius-note`}>{s.note}</div>}
              </div>
            </Wrapper>
          );
        })}
      </div>

      {showSaved && <SavedItemsPanel items={savedItems} onRemove={removeSaved} onClose={() => setShowSaved(false)} user={user} />}

      {user?.partner_user_id && <FamilyActivityPanel items={familyActivity} user={user} />}

      {showMyRsvps && (
        <div className="mb-10" data-testid="my-rsvps-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-black text-forest">My RSVPs</h2>
            <button onClick={() => setShowMyRsvps(false)} className="text-xs font-bold text-terracotta-dark hover:underline" data-testid="my-rsvps-close-btn">
              Close
            </button>
          </div>
          <div className="flex gap-2 mb-4" role="tablist" data-testid="my-rsvps-tabs">
            <button
              type="button"
              role="tab"
              aria-selected={rsvpsTab === "meetups"}
              onClick={() => setRsvpsTab("meetups")}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${rsvpsTab === "meetups" ? "bg-sage text-white" : "bg-white/70 border border-sage/20 text-forest hover:bg-sage-light/60"}`}
              data-testid="my-rsvps-tab-meetups"
            >
              Meet-ups ({myRsvpedMeetups.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={rsvpsTab === "events"}
              onClick={() => setRsvpsTab("events")}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-colors ${rsvpsTab === "events" ? "bg-sage text-white" : "bg-white/70 border border-sage/20 text-forest hover:bg-sage-light/60"}`}
              data-testid="my-rsvps-tab-events"
            >
              Events Interested In ({myRsvpedEvents.length})
            </button>
          </div>
          {rsvpsTab === "meetups" ? (
            myRsvpedMeetups.length === 0 ? (
              <div className="bg-white/70 border border-sage/20 rounded-2xl p-8 text-center text-forest/60" data-testid="my-rsvps-empty">
                You haven't RSVPed to any meet-ups yet. Head over to <Link to="/meetups" className="font-bold text-terracotta-dark hover:underline">Meet-ups</Link> to join one.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myRsvpedMeetups.map((m) => {
                  const when = [m.date, m.start_time].filter(Boolean).join(" · ");
                  return (
                    <Link
                      key={m.event_id}
                      to={`/meetups?event_id=${m.event_id}`}
                      className="block text-left bg-white/80 border border-sage/20 rounded-2xl p-5 shadow-sm hover:shadow-md hover:scale-[1.01] transition-[transform,box-shadow]"
                      data-testid={`my-rsvp-card-${m.event_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta text-white px-2.5 py-1 rounded-full">Meet-up</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2.5 py-1 rounded-full">RSVP'd</span>
                        <RsvpByTag rsvp={m.my_rsvp} user={user} />
                        {user && m.created_by === user.user_id && (
                          <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full" data-testid={`my-rsvp-hosted-badge-${m.event_id}`}>
                            Hosted by you
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading font-bold text-forest text-base">{m.title}</h3>
                      <div className="mt-2 space-y-1 text-xs text-forest/60">
                        {when && (
                          <p className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {when}</p>
                        )}
                        {m.location && (
                          <p className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {m.location}</p>
                        )}
                        {m.creator_name && (
                          <p className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> Hosted by {m.creator_name}</p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )
          ) : (
            myRsvpedEvents.length === 0 ? (
              <div className="bg-white/70 border border-sage/20 rounded-2xl p-8 text-center text-forest/60" data-testid="my-rsvped-events-empty">
                You haven't RSVPed to any events yet. Head over to <Link to="/events" className="font-bold text-terracotta-dark hover:underline">Events</Link> to find one.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-rsvped-events-list">
                {myRsvpedEvents.map((e) => {
                  const whenDate = e.event_date || e.date;
                  const whenTime = e.event_time || e.start_time;
                  const when = [whenDate, whenTime].filter(Boolean).join(" · ");
                  const organizer = e.organizer_name || e.creator_name;
                  return (
                    <Link
                      key={e.event_id}
                      to={`/events/${e.event_id}`}
                      className="block text-left bg-white/80 border border-sage/20 rounded-2xl p-5 shadow-sm hover:shadow-md hover:scale-[1.01] transition-[transform,box-shadow]"
                      data-testid={`my-rsvped-event-card-${e.event_id}`}
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full">Event</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2.5 py-1 rounded-full">Interested</span>
                        <RsvpByTag rsvp={e.my_rsvp} user={user} />
                      </div>
                      <h3 className="font-heading font-bold text-forest text-base">{e.event_name || e.title}</h3>
                      <div className="mt-2 space-y-1 text-xs text-forest/60">
                        {when && (
                          <p className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {when}</p>
                        )}
                        {e.location && (
                          <p className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</p>
                        )}
                        {organizer && (
                          <p className="flex items-center gap-1"><UserIcon className="w-3.5 h-3.5" /> {organizer}</p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <h2 className="text-xl font-black text-forest mb-4" data-testid="next-up-heading">Next up</h2>

          {/* Events sub-section */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black uppercase tracking-wide text-forest/70" data-testid="next-up-events-heading">Events</h3>
            <Link to="/events" className="text-sm font-bold text-terracotta-dark hover:underline flex items-center gap-1" data-testid="see-all-events">
              All events <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3" data-testid="next-up-events-list">
            {upcomingEvents.length === 0 && (
              <div className="bg-white/70 border border-sage/20 rounded-2xl p-6 text-center text-forest/60 text-sm" data-testid="no-events-msg">
                No upcoming events yet — check back soon!
              </div>
            )}
            {upcomingEvents.map((e) => {
              const whenDate = (e.event_date || e.date || "").trim();
              const whenTime = (e.event_time || e.start_time || "").trim();
              const when = [whenDate, whenTime].filter(Boolean).join(" · ");
              return (
              <Link key={e.event_id} to={`/events/${e.event_id}`} className="block bg-white/70 border border-sage/20 rounded-2xl p-5 shadow-sm hover:shadow-md hover:scale-[1.01] transition-[transform,box-shadow]" data-testid={`dash-event-${e.event_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-heading font-bold text-forest">{e.title}</h4>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-forest/60">
                      {when && (
                        <span className="flex items-center gap-1" data-testid={`dash-event-when-${e.event_id}`}>
                          <CalendarDays className="w-3.5 h-3.5" /> {when}
                        </span>
                      )}
                      {e.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>
                      )}
                    </div>
                  </div>
                  {e.my_rsvp && (
                    <span className="shrink-0 text-xs font-bold bg-sage-light text-sage-dark px-3 py-1 rounded-full" data-testid={`dash-rsvp-badge-${e.event_id}`}>
                      RSVP'd
                    </span>
                  )}
                </div>
              </Link>
              );
            })}
          </div>

          {/* Meet-ups sub-section */}
          <div className="flex items-center justify-between mt-6 mb-3">
            <h3 className="text-sm font-black uppercase tracking-wide text-forest/70" data-testid="next-up-meetups-heading">Meet-ups</h3>
            <Link to="/meetups" className="text-sm font-bold text-terracotta-dark hover:underline flex items-center gap-1" data-testid="see-all-meetups">
              All meet-ups <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3" data-testid="next-up-meetups-list">
            {upcomingMeetups.length === 0 && (
              <div className="bg-white/70 border border-sage/20 rounded-2xl p-6 text-center text-forest/60 text-sm" data-testid="no-meetups-msg">
                No upcoming meet-ups yet — <Link to="/meetups" className="font-bold text-terracotta-dark hover:underline">host one</Link>!
              </div>
            )}
            {upcomingMeetups.map((m) => {
              const whenDate = (m.event_date || m.date || "").trim();
              const whenTime = (m.event_time || m.start_time || "").trim();
              const when = [whenDate, whenTime].filter(Boolean).join(" · ");
              return (
              <Link key={m.event_id} to={`/meetups?event_id=${m.event_id}`} className="block bg-white/70 border border-sage/20 rounded-2xl p-5 shadow-sm hover:shadow-md hover:scale-[1.01] transition-[transform,box-shadow]" data-testid={`dash-meetup-${m.event_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta text-white px-2.5 py-1 rounded-full">Meet-up</span>
                      {user && m.created_by === user.user_id && (
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full" data-testid={`dash-meetup-hosted-badge-${m.event_id}`}>
                          Hosted by you
                        </span>
                      )}
                    </div>
                    <h4 className="font-heading font-bold text-forest">{m.title}</h4>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-forest/60">
                      {when && (
                        <span className="flex items-center gap-1" data-testid={`dash-meetup-when-${m.event_id}`}>
                          <CalendarDays className="w-3.5 h-3.5" /> {when}
                        </span>
                      )}
                      {m.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {m.location}</span>
                      )}
                    </div>
                  </div>
                  {m.my_rsvp && (
                    <span className="shrink-0 text-xs font-bold bg-sage-light text-sage-dark px-3 py-1 rounded-full" data-testid={`dash-meetup-rsvp-badge-${m.event_id}`}>
                      RSVP'd
                    </span>
                  )}
                </div>
              </Link>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-8 mb-4">
            <h2 className="text-xl font-black text-forest">Announcements</h2>
            <Link to="/resources" className="text-sm font-bold text-terracotta-dark hover:underline flex items-center gap-1" data-testid="see-resources">
              Resource hub <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.announcement_id} className="bg-white/70 border border-sage/20 rounded-2xl p-5" data-testid={`dash-ann-${a.announcement_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Megaphone className="w-4 h-4 text-terracotta-dark" />
                  <h3 className="font-heading font-bold text-forest text-sm">{a.title}</h3>
                  {a.pinned && <span className="text-[10px] font-bold bg-terracotta-light text-terracotta-dark px-2 py-0.5 rounded-full uppercase">Pinned</span>}
                </div>
                <p className="text-sm text-forest/70 leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h2 className="text-xl font-black text-forest">Quick links</h2>
          {[
            { to: "/vendors", icon: Store, title: "Browse vendors", desc: "Tutors, classes & more", tint: "bg-sage-light text-sage-dark", testid: "quick-vendors" },
            { to: "/events", icon: CalendarDays, title: "Event calendar", desc: "RSVP for activities", tint: "bg-terracotta-light text-terracotta-dark", testid: "quick-events" },
            { to: "/resources", icon: BookOpen, title: "Resource hub", desc: "Printables & schedules", tint: "bg-sage-light text-sage-dark", testid: "quick-resources" },
          ].map((q) => (
            <Link key={q.to} to={q.to} className="flex items-center gap-4 bg-white/70 border border-sage/20 rounded-2xl p-5 shadow-sm hover:shadow-md hover:scale-[1.01] transition-[transform,box-shadow]" data-testid={q.testid}>
              <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${q.tint}`}>
                <q.icon className="w-5 h-5" />
              </span>
              <div>
                <div className="font-heading font-bold text-forest">{q.title}</div>
                <div className="text-xs text-forest/60">{q.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {user?.role === "parent" && !user?.admin_impersonating && <div className="mt-10"><AddRoleCard /></div>}

      <Dialog open={!!openMeetup} onOpenChange={(o) => { if (!o) setOpenMeetup(null); }}>
        <DialogContent className="rounded-3xl bg-cream max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="rsvp-detail-dialog">
          {openMeetup && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-forest text-xl" data-testid="rsvp-detail-title">
                  {openMeetup.title}
                </DialogTitle>
                <DialogDescription className="text-forest/60">
                  Hosted by <strong>{openMeetup.creator_name || "the host"}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 min-w-0">
                <div className="bg-white/80 border border-sage/20 rounded-2xl p-4 space-y-2 text-sm min-w-0" data-testid="rsvp-detail-info">
                  {openMeetup.date && (
                    <p className="flex items-center gap-2 text-forest/80">
                      <CalendarDays className="w-4 h-4 text-sage-dark" />
                      <span><strong>{openMeetup.date}</strong>{openMeetup.start_time ? ` · ${openMeetup.start_time}` : ""}</span>
                    </p>
                  )}
                  {openMeetup.location && (
                    <p className="flex items-center gap-2 text-forest/80">
                      <MapPin className="w-4 h-4 text-sage-dark" />
                      <span>{openMeetup.location}</span>
                    </p>
                  )}
                  {openMeetup.age_range && (
                    <p className="flex items-center gap-2 text-forest/80">
                      <Clock className="w-4 h-4 text-sage-dark" />
                      <span>Age range: {openMeetup.age_range}</span>
                    </p>
                  )}
                  <p className="flex items-center gap-2 text-forest/80">
                    <Users className="w-4 h-4 text-sage-dark" />
                    <span>{openMeetup.capacity == null ? `${openMeetup.spots_filled ?? 0} joined · No limit` : `${openMeetup.spots_filled ?? 0}/${openMeetup.capacity} spots filled`}</span>
                  </p>
                  {openMeetup.description && (
                    <p className="text-forest/70 leading-relaxed pt-2 border-t border-sage/10">
                      {openMeetup.description}
                    </p>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Questions & chat</h4>
                  <MeetupChatPanel eventId={openMeetup.event_id} user={user} hostUserId={openMeetup.created_by} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BundlePendingCard({ roles }) {
  return (
    <div className="mb-8 rounded-2xl border-2 border-terracotta/40 bg-terracotta-light/40 p-4 max-w-xl" data-testid="dashboard-bundle-pending-card">
      <div className="flex items-start gap-3">
        <Clock className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs font-black uppercase tracking-wide text-terracotta-dark">Bundle profiles awaiting approval</p>
          <ul className="mt-1 space-y-0.5">
            {roles.map((r) => (
              <li key={r.role} className="text-sm font-bold text-forest" data-testid={`dashboard-bundle-pending-role-${r.role}`}>
                {r.label} — pending admin approval
              </li>
            ))}
          </ul>
          <p className="text-xs text-forest/70 mt-0.5">Your Parent profile is live. These go live once approved.</p>
        </div>
      </div>
    </div>
  );
}

function SavedItemsPanel({ items, onRemove, onClose, user }) {
  return (
    <div className="mb-10" data-testid="saved-items-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black text-forest">Liked Vendors and Marketplaces</h2>
        <button onClick={onClose} className="text-xs font-bold text-terracotta-dark hover:underline" data-testid="saved-items-close-btn">Close</button>
      </div>
      {items.length === 0 ? (
        <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="saved-items-empty">
          Nothing saved yet — tap "Add to my List" on any vendor or marketplace card.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => <SavedItemCard key={`${i.item_type}:${i.item_id}`} item={i} onRemove={onRemove} user={user} />)}
        </div>
      )}
    </div>
  );
}

function SavedItemCard({ item, onRemove, user }) {
  return (
    <div className="bg-white/80 border border-sage/20 rounded-2xl overflow-hidden shadow-sm" data-testid={`saved-item-${item.item_type}-${item.item_id}`}>
      <Link to={item.link} className="block">
        <div className="h-28 bg-sage-light flex items-center justify-center overflow-hidden">
          <img src={item.logo ? fileUrl(item.logo) : "/logo.png"} alt={item.name} className={item.logo ? "w-full h-full object-contain" : "max-h-16 opacity-80"} />
        </div>
        <div className="p-4 pb-2">
          <span className="text-[10px] font-bold uppercase tracking-wide bg-cream text-forest/70 border border-sage/20 px-2 py-0.5 rounded-full">
            {item.item_type === "vendor" ? "Vendor" : "Marketplace"}
          </span>
          <h3 className="font-heading font-bold text-forest mt-2 leading-snug">{item.name || "Unnamed"}</h3>
          {item.location && <p className="text-xs text-forest/60 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {item.location}</p>}
          {user?.partner_user_id && item.added_by_id && item.added_by_id !== user.user_id && (
            <p className="text-[11px] text-sage-dark font-semibold mt-1" data-testid={`saved-item-added-by-${item.item_id}`}>Added by {item.added_by_name || "your partner"}</p>
          )}
        </div>
      </Link>
      <div className="px-4 pb-4">
        <button type="button" onClick={() => onRemove(item)}
          className="text-xs font-bold px-3 py-1.5 rounded-full border border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light"
          data-testid={`saved-item-remove-${item.item_type}-${item.item_id}`}>
          Remove from List
        </button>
      </div>
    </div>
  );
}

function RsvpByTag({ rsvp, user }) {
  if (!rsvp || !user || rsvp.parent_user_id === user.user_id) return null;
  return (
    <span className="text-[10px] font-bold uppercase tracking-wide bg-cream text-forest/70 border border-sage/20 px-2.5 py-1 rounded-full" data-testid="rsvp-by-partner-tag">
      RSVP'd by {(rsvp.parent_name || "your partner").split(" ")[0]}
    </span>
  );
}

function FamilyActivityPanel({ items, user }) {
  return (
    <div className="mb-10" data-testid="family-activity-panel">
      <h2 className="text-xl font-black text-forest mb-4">Family activity</h2>
      {items.length === 0 ? (
        <div className="bg-white/60 border border-sage/20 rounded-2xl p-6 text-center text-sm text-forest/60" data-testid="family-activity-empty">
          Nothing yet — RSVPs, saved cards and meet-ups from either parent will show up here.
        </div>
      ) : (
        <ul className="bg-white/80 border border-sage/20 rounded-2xl divide-y divide-sage/10">
          {items.map((a, idx) => (
            <li key={`${a.created_at}-${idx}`} className="px-4 py-3 text-sm flex items-start justify-between gap-3" data-testid={`family-activity-item-${idx}`}>
              <span className="text-forest">
                <strong>{a.actor_id === user.user_id ? "You" : (a.actor_name || "Your partner").split(" ")[0]}</strong>{" "}
                {a.link ? <Link to={a.link} className="hover:underline">{a.label}</Link> : a.label}
              </span>
              <span className="text-[11px] text-forest/40 shrink-0">{new Date(a.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
